import { useEffect, useLayoutEffect, useRef, useState } from 'react';

// useLayoutEffect on the client, useEffect during SSR to avoid warnings.
const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;

/**
 * useResponsiveNav — content-aware responsive navigation.
 *
 * The classic `@media (max-width: 768px)` approach fails when nav content grows
 * (more links, longer i18n labels, token chip, bell, avatar) — at ~900px the
 * content can overflow even though we're still well above the breakpoint.
 *
 * This hook measures the actual content instead. It watches three elements:
 *
 *   - container (the nav's inner flex row)
 *   - logo     (always visible, takes up fixed space)
 *   - nav      (the desktop nav links — the part that can overflow)
 *
 * and flips `isCompact` on/off based on whether
 *   logo.offsetWidth + nav.scrollWidth + gap
 * fits inside container.clientWidth.
 *
 * Hysteresis: once compact, we require a larger margin before going back to
 * desktop than was required to go compact in the first place. Without this,
 * the nav can flicker rapidly at the exact boundary as the measurements shift
 * by a pixel between paints.
 *
 * Initial value: we guess based on viewport width (assume compact below 900px)
 * so SSR / first paint is mostly right; the ResizeObserver corrects
 * immediately after mount.
 *
 * Usage:
 *   const { containerRef, logoRef, navRef, isCompact } = useResponsiveNav<
 *     HTMLDivElement,    // NavInner (MUI Box → div)
 *     HTMLAnchorElement, // NavLogo (react-router Link → a)
 *     HTMLElement        // NavLinks (styled nav)
 *   >();
 *
 *   <NavInner ref={containerRef}>
 *     <NavLogo ref={logoRef}>…</NavLogo>
 *     <NavLinks ref={navRef} $hidden={isCompact} aria-hidden={isCompact} inert={isCompact}>…</NavLinks>
 *     {isCompact && <Hamburger />}
 *   </NavInner>
 *
 * The element-type generics default to `HTMLElement` so callers who don't
 * care can omit them entirely; callers who pass specific elements (like
 * react-router Link → HTMLAnchorElement) get correctly-typed refs without
 * casts.
 */
export function useResponsiveNav<
  TContainer extends HTMLElement = HTMLElement,
  TLogo extends HTMLElement = HTMLElement,
  TNav extends HTMLElement = HTMLElement,
>({
  /** Flex gap between logo and nav, in px. Match the CSS `gap` on the container. */
  gap = 16,
  /**
   * Hysteresis margins. When currently in desktop mode, we go compact if we
   * overflow by more than `overflowToCompact` px. When currently compact,
   * we go back to desktop only if we have at least `slackToDesktop` px free.
   * `slackToDesktop > overflowToCompact` so we don't flicker at the boundary.
   */
  overflowToCompact = 0,
  slackToDesktop = 32,
}: {
  gap?: number;
  overflowToCompact?: number;
  slackToDesktop?: number;
} = {}) {
  const containerRef = useRef<TContainer | null>(null);
  const logoRef = useRef<TLogo | null>(null);
  const navRef = useRef<TNav | null>(null);

  const [isCompact, setIsCompact] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    // Sensible default so first paint is close to correct. Corrected on mount.
    return window.innerWidth < 900;
  });

  useIsomorphicLayoutEffect(() => {
    const container = containerRef.current;
    const nav = navRef.current;
    const logo = logoRef.current;
    if (!container || !nav || !logo) return;

    // We keep our own copy of the state inside the closure so the measure
    // function always sees the latest value without us having to re-bind
    // the observer on every re-render.
    let currentCompact = isCompact;

    const measure = () => {
      const available = container.clientWidth;
      // scrollWidth reflects the intrinsic content width even when the
      // element is position:absolute and visually hidden (our `$hidden`
      // state) — so this works whether or not the nav is currently
      // participating in the flex flow.
      const needed = logo.offsetWidth + nav.scrollWidth + gap;

      let next: boolean;
      if (currentCompact) {
        // Only flip back to desktop if there's comfortable slack.
        next = needed + slackToDesktop > available;
      } else {
        // Go compact as soon as we'd overflow (optionally plus a margin).
        next = needed + overflowToCompact > available;
      }

      if (next !== currentCompact) {
        currentCompact = next;
        setIsCompact(next);
      }
    };

    // Measure once synchronously so we don't flash the wrong layout.
    measure();

    const ro = new ResizeObserver(measure);
    ro.observe(container);
    ro.observe(nav);
    ro.observe(logo);

    return () => ro.disconnect();
    // Deps intentionally exclude `isCompact` — the measure function handles
    // its own change detection via the closed-over `currentCompact` variable.
  }, [gap, overflowToCompact, slackToDesktop]);

  return { containerRef, logoRef, navRef, isCompact };
}
